import Loading from 'components/loading'
import { IAgencyGroup } from 'pages/agency-group-management/interface'
import React, { useEffect, useState } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'
import { headerAgencyByAgencyGroupTable } from './header'

import IconDelete from 'assets/images/icon-delete.svg'
import IconDot from 'assets/images/icon-dot.svg'
import { useOutsideClick } from 'components/hooks/useOutsideClick'
import SingleRowActionBase from 'components/partials/singleRowActionBase'
import { useLazyQuery, useMutation } from '@apollo/client'
import { DELETE_AGENCY_FROM_AGENCY_GROUP } from 'queries/admin/agencyGroup'
import { notify } from 'components/toastify'
import { STATUS } from 'constants/messages'
import AddAgenciesToAgencyGroup from '../dialogs/addAgenciesToAgencyGroup'
import { GET_AGENCIES_BY_AGENCY_GROUP_ID, IAgency } from 'queries/admin/agency'
import UploadAgencyFileDialog from '../dialogs/uploadAgencyFile'
import { useSession } from 'helpers/checkSession'

interface Props {
  agencyGroup: IAgencyGroup
  isOpenAddAgencyDialog: boolean
  isOpenUploadCSVFileDialog: boolean
  handleCloseUploadCSVFileDialog: () => void
  handleCloseAddAgencyDialog: () => void
}

const LIMIT = 5

const AgencyListTable: React.FC<Props> = ({
  agencyGroup,
  isOpenAddAgencyDialog,
  isOpenUploadCSVFileDialog,
  handleCloseAddAgencyDialog,
  handleCloseUploadCSVFileDialog,
}) => {
  const { handleErrorSession } = useSession()
  const [tableData, setTableData] = useState<IAgency[]>([])
  const [isTableLoading] = useState<boolean>(false)
  const [isShowAction, setIsShowAction] = useState<boolean>(false)
  const [hasMoreData, setHasMoreData] = useState<boolean>(true)
  const [currentRowSelected, setCurrentRowSelected] = useState<IAgency>()

  const [
    getAgenciesByAgencyGroupQuery,
    { refetch: refetchAgencies, fetchMore: fetchMoreAgencies },
  ] = useLazyQuery(GET_AGENCIES_BY_AGENCY_GROUP_ID, {
    variables: {
      agencyGroupId: agencyGroup.agencyGroupId,
      skip: 0,
      take: LIMIT,
    },
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {
      if (!data) return
      setHasMoreData(data.getAgenciesByAgencyGroupId.length === LIMIT)
      setTableData(data.getAgenciesByAgencyGroupId)
    },
    onError: (error) => {
      if (error.message) {
        handleErrorSession(error, error.message)
      } else {
        notify('Something wrong', STATUS.ERROR)
      }
    },
  })

  const [deleteAgencyFromAgencyGroup] = useMutation(DELETE_AGENCY_FROM_AGENCY_GROUP, {
    variables: {
      data: {
        agencyGroupId: agencyGroup.agencyGroupId,
        agencyId: currentRowSelected?.agencyId ?? '',
      },
    },
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      if (data.deleteSingleAgencyFromAgencyGroup) {
        notify('Delete successfully!', STATUS.SUCCESS)
        handleRefetchAgencies()
      } else {
        notify('Something wrong', STATUS.ERROR)
      }
    },
    onError: (error) => {
      if (error.message) {
        handleErrorSession(error, error.message)
      } else {
        notify('Something wrong', STATUS.ERROR)
      }
    },
  })

  const handleRefetchAgencies = () => {
    refetchAgencies({ skip: 0, take: LIMIT })
  }

  const ref = useOutsideClick(() => {
    setIsShowAction(false)
  })

  const handleFetchMore = () => {
    if (!fetchMoreAgencies) return

    fetchMoreAgencies({
      variables: {
        skip: tableData.length,
        take: LIMIT,
      },
    }).then(({ data: fetchMoreData }) => {
      if (fetchMoreData.getAgenciesByAgencyGroupId) {
        setTableData((prev) => [...prev, ...fetchMoreData.getAgenciesByAgencyGroupId])
      }
    })
  }

  const onHandleClickShowAction = (rowItem: IAgency) => {
    if (currentRowSelected?.agencyId === rowItem.agencyId) {
      setIsShowAction(!isShowAction)
    } else {
      setIsShowAction(true)
    }
    setCurrentRowSelected(rowItem)
  }

  const onClickDeleteAction = () => {
    if (agencyGroup.agencyGroupId && currentRowSelected?.agencyId) {
      deleteAgencyFromAgencyGroup()
    }
  }

  useEffect(() => {
    getAgenciesByAgencyGroupQuery()
  }, [])

  return (
    <div>
      <table className="w-full text-left border-2 border-neutral-6 rounded-t-lg overflow-hidden">
        <thead className="bg-primary-shade4 text-body1 text-neutral-2 md:pr-4">
          <tr>
            {headerAgencyByAgencyGroupTable.map((item: any, idx) => (
              <th key={idx} className={item.headerStyles}>
                <div className={`flex `}>
                  <span className={`${item.isOneLineRes && 'lg:mt-[-3.5px]'}`}>{item.name}</span>
                  <div className={`${item.isOneLineRes && 'lg:mt-[-3.5px]'}`}></div>
                </div>
              </th>
            ))}
          </tr>
        </thead>
      </table>

      {isTableLoading && <Loading className="relative py-6" height={30} width={30} />}

      {tableData?.length === 0 && !isTableLoading ? (
        <p className="py-6 text-center text-body1 text-neutral-4">No data available.</p>
      ) : (
        Boolean(tableData !== null && tableData?.length > 0 && !isTableLoading) && (
          <InfiniteScroll
            dataLength={tableData?.length}
            next={handleFetchMore}
            hasMore={hasMoreData}
            height={'max-content'}
            style={{ maxHeight: 300, minHeight: 300 }}
            loader={''}
            endMessage={''}
            className="cus-scrollbar"
          >
            {tableData?.map((data: IAgency, index: number) => (
              <table key={index} className="cursor-pointer w-full">
                <tbody>
                  <tr className="h-[72px] w-full hover:bg-neutral-7 pt-5">
                    <td className={` ${headerAgencyByAgencyGroupTable[0].rowWid}`}>
                      <div>
                        <span>{data.agencyKey}</span>
                      </div>
                    </td>
                    <td className={`${headerAgencyByAgencyGroupTable[1].rowWid}`}>
                      <div>
                        <span>{data.agencyName}</span>
                      </div>
                    </td>
                    <td className={`${headerAgencyByAgencyGroupTable[2].rowWid}`}>
                      <button
                        className="min-w-[24px] mb-auto"
                        onClick={() => onHandleClickShowAction(data)}
                      >
                        <div className="relative">
                          <img src={IconDot} alt="Icon-Dot" />
                          {currentRowSelected?.agencyId === data.agencyId && isShowAction && (
                            <div
                              className={`absolute right-[2px] h-auto p-3 border shadow-2xl w-[220px] border-neutral-7 bg-neutral-8 rounded-xl z-10`}
                              ref={ref}
                            >
                              <SingleRowActionBase
                                handleAction={onClickDeleteAction}
                                icon={IconDelete}
                                action="Delete"
                                txt="Delete"
                              />
                            </div>
                          )}
                        </div>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            ))}
          </InfiniteScroll>
        )
      )}

      {isOpenAddAgencyDialog && (
        <AddAgenciesToAgencyGroup
          currentAgencyGroup={agencyGroup}
          handleCloseDialog={() => {
            handleCloseAddAgencyDialog()
            getAgenciesByAgencyGroupQuery()
          }}
          isOpenDialog={isOpenAddAgencyDialog}
        />
      )}

      {isOpenUploadCSVFileDialog && (
        <UploadAgencyFileDialog
          agencyGroup={agencyGroup}
          isOpenDialog={isOpenUploadCSVFileDialog}
          handleCloseDialog={() => {
            getAgenciesByAgencyGroupQuery()
            handleCloseUploadCSVFileDialog()
          }}
        />
      )}
    </div>
  )
}

export default AgencyListTable
